import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { backendApi, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/dates';
import { useModalEscape } from '../lib/useModalEscape';

/**
 * Adoption funnel — where incoming users stall between arriving and actively
 * using the plugin command center. Two funnels because the paths differ: a
 * registered user goes signup → verify → onboard → sandbox → launch → chat →
 * build, while a public try-landing sandbox has no account and starts already
 * inside the product. Stages are NOT forced monotonic (an own-site plugin user
 * can chat without ever making a sandbox), so a later stage may exceed an
 * earlier one — that gap is the signal, not a bug.
 */

type Row = Record<string, number | string>;
interface FunnelResp { days: number; registered: Row; publicTrial: Row }

const WINDOWS = [{ k: '7', label: '7d' }, { k: '30', label: '30d' }, { k: '90', label: '90d' }, { k: 'all', label: 'All time' }];

const REG_STAGES: [string, string, string][] = [
  ['signed_up', 'Signed up', 'Created an account'],
  ['onboarded', 'Completed onboarding', 'Finished the setup wizard'],
  ['created_any', 'Created sandbox / connected site', 'Provisioned a sandbox or connected their own site'],
  ['launched_any', 'Launched sandbox / site', 'The sandbox or site actually loaded / phoned home'],
  ['chatted', 'Used the command center', 'Sent a chat / consumed credits'],
];
const TRY_STAGES: [string, string, string][] = [
  ['trial_started', 'Trial started', 'Public try-landing sandbox created (no account)'],
  ['launched', 'Launched', 'The sandbox loaded / phoned home'],
  ['chatted', 'Used the command center', 'Sent a chat / consumed credits'],
];

const n = (v: number | string | undefined) => Number(v ?? 0);

function pct(part: number, whole: number): string {
  if (!whole) return '0%';
  return `${Math.round((part / whole) * 100)}%`;
}

function FunnelChart({ stages, data, onPick }: {
  stages: [string, string, string][]; data: Row; onPick: (stage: string, label: string, metCount: number, total: number) => void;
}) {
  const first = n(data[stages[0][0]]);
  return (
    <div className="funnel">
      {stages.map(([key, label, hint], i) => {
        const val = n(data[key]);
        const prev = i === 0 ? val : n(data[stages[i - 1][0]]);
        // Drop from the previous stage — the bottleneck signal.
        const dropped = Math.max(prev - val, 0);
        const width = first ? Math.max((val / first) * 100, 2) : 0;
        return (
          <button type="button" className="funnel-row funnel-row-btn" key={key}
            onClick={() => onPick(key, label, val, first)} title="Click to see the accounts in this step">
            <div className="funnel-label">
              <span className="funnel-stage">{label}</span>
              <span className="funnel-hint muted small">{hint}</span>
            </div>
            <div className="funnel-track">
              <div className="funnel-bar" style={{ width: `${width}%` }}>
                <span className="funnel-count">{val.toLocaleString()}</span>
              </div>
            </div>
            <div className="funnel-meta">
              <span className="funnel-pct">{pct(val, first)}</span>
              {i > 0 && dropped > 0 && (
                <span className="funnel-drop" title={`${dropped} did not reach this step`}>−{dropped.toLocaleString()}</span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

interface StageAccount {
  id: string; email: string; name?: string | null; plan_id?: string | null;
  account_type?: string | null; created_at: string; last_active?: string | null;
}
function StageAccountsModal({ funnel, stage, label, metCount, total, days, onClose }: {
  funnel: string; stage: string; label: string; metCount: number; total: number; days: string; onClose: () => void;
}) {
  useModalEscape(onClose);
  const [tab, setTab] = useState<'met' | 'not'>('met');
  const [rows, setRows] = useState<StageAccount[] | null>(null);
  const [error, setError] = useState('');
  const notCount = Math.max(total - metCount, 0);
  useEffect(() => {
    setRows(null); setError('');
    backendApi.get<{ accounts: StageAccount[] }>(`/license/admin/funnel/accounts?funnel=${funnel}&stage=${encodeURIComponent(stage)}&days=${days}&met=${tab === 'met'}`)
      .then((d) => setRows(d.accounts || []))
      .catch((err: ApiError) => setError(err.message));
  }, [funnel, stage, days, tab]);
  return createPortal(
    <div className="modal-scrim" onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <div className="modal account-modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <h2 className="card-title" style={{ margin: 0 }}>{label}</h2>
          <button className="btn btn-sm" onClick={onClose}>Close</button>
        </div>
        <div className="account-modal-body">
          <div className="seg" style={{ marginBottom: 14 }}>
            <button className={'seg-btn' + (tab === 'met' ? ' on' : '')} onClick={() => setTab('met')}>Reached this step ({metCount.toLocaleString()})</button>
            <button className={'seg-btn' + (tab === 'not' ? ' on' : '')} onClick={() => setTab('not')}>Didn’t reach it ({notCount.toLocaleString()})</button>
          </div>
          {error && <div className="alert alert-error">{error}</div>}
          {!rows ? <p className="muted">Loading…</p> : rows.length === 0 ? <p className="muted">{tab === 'met' ? 'No accounts in this step.' : 'Everyone in the cohort reached this step.'}</p> : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Email</th><th>Plan</th><th>Signed up</th><th>Last active</th></tr></thead>
                <tbody>
                  {rows.map((a) => (
                    <tr key={a.id}>
                      <td>{a.email}{a.name && <span className="muted small block">{a.name}</span>}</td>
                      <td><span className="badge badge-purple">{a.plan_id || 'free'}</span>
                        {a.account_type && a.account_type !== 'customer' && <span className="muted small block">{a.account_type}</span>}</td>
                      <td className="muted">{fmtDateTime(a.created_at)}</td>
                      <td className="muted">{a.last_active ? fmtDateTime(a.last_active) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>, document.body);
}

/**
 * The animated journey: users (balls) stream down from "Signed up", pass the
 * gates, split into the two routes they can take — Sandbox vs Connect a site —
 * converge on the command center and fall into the "Built site" container.
 * Balls that stall at any gate peel off into the "Stalled" bin, so the shape of
 * the loss is visible at a glance. Ball flow is illustrative (rates match the
 * conversions); the numbers on each box are the exact counts.
 */
function FunnelFlow({ data }: { data: Row }) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const num = (k: string) => Number(data[k] ?? 0);
    const signedUp = Math.max(num('signed_up'), 1);
    const counts: Record<string, number> = {
      signed_up: num('signed_up'), onboarded: num('onboarded'),
      sandbox_created: num('sandbox_created'), site_connected: num('site_connected'),
      chatted: num('chatted'),
    };
    const stalledCount = Math.max(counts.signed_up - counts.chatted, 0);

    const cssv = getComputedStyle(document.documentElement);
    const cv = (v: string, fb: string) => (cssv.getPropertyValue(v).trim() || fb);
    const COL = {
      text: cv('--color-text-secondary', '#c9c9c9'),
      muted: cv('--color-text-tertiary', '#8a8a8a'),
      surface: cv('--color-surface-raised', '#1b1b1b'),
      border: cv('--color-border-default', '#333'),
      sandbox: '#8A38F5', site: '#3B82F6', goal: '#22C55E', stall: '#EF4444',
    };
    const H = 470;
    let W = 0, dpr = Math.min(window.devicePixelRatio || 1, 2);

    type Node = { x: number; y: number; label: string; count: number; color: string };
    let nodes: Record<string, Node> = {};
    function layout() {
      const cx = W / 2;
      const lx = Math.max(W * 0.26, 110);
      const rx = Math.min(W * 0.74, W - 110);
      nodes = {
        signed_up: { x: cx, y: 44, label: 'Signed up', count: counts.signed_up, color: COL.text },
        onboarded: { x: cx, y: 148, label: 'Onboarded', count: counts.onboarded, color: COL.text },
        sandbox_created: { x: lx, y: 258, label: 'Created sandbox', count: counts.sandbox_created, color: COL.sandbox },
        site_connected: { x: rx, y: 258, label: 'Connected site', count: counts.site_connected, color: COL.site },
        chatted: { x: cx, y: 372, label: 'Used command center', count: counts.chatted, color: COL.goal },
        stalled: { x: Math.min(W - 78, W * 0.9), y: 180, label: 'Stalled', count: stalledCount, color: COL.stall },
      };
    }

    const clamp01 = (x: number) => (Number.isFinite(x) ? Math.min(Math.max(x, 0), 1) : 0);
    function makePath(): { path: string[]; color: string } {
      const p = ['signed_up'];
      let color = COL.muted;
      if (Math.random() < clamp01(counts.onboarded / signedUp)) {
        p.push('onboarded');
        const createTotal = counts.sandbox_created + counts.site_connected;
        if (createTotal > 0 && Math.random() < clamp01(createTotal / Math.max(counts.onboarded, 1))) {
          const sandbox = Math.random() < counts.sandbox_created / createTotal;
          color = sandbox ? COL.sandbox : COL.site;
          p.push(sandbox ? 'sandbox_created' : 'site_connected');
          if (Math.random() < clamp01(counts.chatted / Math.max(createTotal, 1))) p.push('chatted');
          else p.push('stalled');
        } else p.push('stalled');
      } else p.push('stalled');
      return { path: p, color };
    }

    type Ball = { path: string[]; seg: number; t: number; color: string; r: number };
    const balls: Ball[] = [];
    const arrivals: Record<string, number> = {}; // recent-arrival glow per node
    function spawn() {
      if (balls.length > 70) return;
      const { path, color } = makePath();
      balls.push({ path, seg: 0, t: 0, color, r: 3 + Math.random() * 1.5 });
    }

    function roundRect(x: number, y: number, w: number, h: number, r: number) {
      ctx!.beginPath();
      ctx!.moveTo(x + r, y);
      ctx!.arcTo(x + w, y, x + w, y + h, r);
      ctx!.arcTo(x + w, y + h, x, y + h, r);
      ctx!.arcTo(x, y + h, x, y, r);
      ctx!.arcTo(x, y, x + w, y, r);
      ctx!.closePath();
    }

    const EDGES: [string, string][] = [
      ['signed_up', 'onboarded'],
      ['onboarded', 'sandbox_created'], ['onboarded', 'site_connected'],
      ['sandbox_created', 'chatted'], ['site_connected', 'chatted'],
    ];
    const BW = 148, BH = 42;

    function draw() {
      ctx!.clearRect(0, 0, W, H);
      // connectors
      ctx!.lineWidth = 1.5;
      ctx!.strokeStyle = COL.border;
      for (const [a, b] of EDGES) {
        const na = nodes[a], nb = nodes[b];
        ctx!.beginPath(); ctx!.moveTo(na.x, na.y + BH / 2); ctx!.lineTo(nb.x, nb.y - BH / 2); ctx!.stroke();
      }
      // balls
      for (const ball of balls) {
        const a = nodes[ball.path[ball.seg]];
        const b = nodes[ball.path[ball.seg + 1]] || a;
        const ease = ball.t;
        const x = a.x + (b.x - a.x) * ease;
        const y = (a.y + BH / 2) + ((b.y - BH / 2) - (a.y + BH / 2)) * ease;
        ctx!.beginPath(); ctx!.arc(x, y, ball.r, 0, Math.PI * 2);
        ctx!.fillStyle = ball.color; ctx!.globalAlpha = 0.9; ctx!.fill(); ctx!.globalAlpha = 1;
      }
      // boxes
      for (const key of Object.keys(nodes)) {
        const nd = nodes[key];
        const x = nd.x - BW / 2, y = nd.y - BH / 2;
        const fill = clamp01(nd.count / signedUp);
        const glow = arrivals[key] || 0;
        // base
        roundRect(x, y, BW, BH, 9);
        ctx!.fillStyle = COL.surface; ctx!.fill();
        // conversion fill (left portion of the box)
        if (fill > 0) {
          ctx!.save(); roundRect(x, y, BW, BH, 9); ctx!.clip();
          ctx!.globalAlpha = 0.18 + glow * 0.25;
          ctx!.fillStyle = nd.color;
          ctx!.fillRect(x, y, BW * (key === 'stalled' ? clamp01(nd.count / signedUp) : fill), BH);
          ctx!.restore();
        }
        roundRect(x, y, BW, BH, 9);
        ctx!.lineWidth = 1; ctx!.strokeStyle = glow > 0.02 ? nd.color : COL.border; ctx!.stroke();
        // text
        ctx!.fillStyle = COL.muted; ctx!.font = '600 10px system-ui, sans-serif'; ctx!.textBaseline = 'middle';
        ctx!.textAlign = 'left';
        ctx!.fillText(nd.label.toUpperCase(), x + 12, y + 15);
        ctx!.fillStyle = nd.color; ctx!.font = '700 17px system-ui, sans-serif';
        ctx!.fillText(nd.count.toLocaleString(), x + 12, y + 31);
        if (arrivals[key]) arrivals[key] = Math.max(0, arrivals[key] - 0.03);
      }
    }

    let raf = 0, acc = 0, last = performance.now();
    function frame(now: number) {
      const dt = Math.min((now - last) / 1000, 0.05); last = now;
      acc += dt;
      while (acc > 0.2) { spawn(); acc -= 0.2; }
      for (let i = balls.length - 1; i >= 0; i--) {
        const ball = balls[i];
        ball.t += dt * 0.85;
        if (ball.t >= 1) {
          ball.t = 0; ball.seg++;
          const arrivedKey = ball.path[ball.seg];
          if (arrivedKey) arrivals[arrivedKey] = 1;
          if (ball.seg >= ball.path.length - 1) balls.splice(i, 1);
        }
      }
      draw();
      raf = requestAnimationFrame(frame);
    }

    function resize() {
      W = wrap!.clientWidth || 800;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas!.width = Math.round(W * dpr); canvas!.height = Math.round(H * dpr);
      canvas!.style.width = W + 'px'; canvas!.style.height = H + 'px';
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      layout();
      if (reduce) draw();
    }
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);
    resize();
    if (!reduce) raf = requestAnimationFrame(frame);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [data]);

  return (
    <div ref={wrapRef} className="funnel-flow-wrap">
      <canvas ref={canvasRef} />
      <div className="funnel-flow-legend">
        <span><i style={{ background: '#8A38F5' }} /> Sandbox route</span>
        <span><i style={{ background: '#3B82F6' }} /> Connect-a-site route</span>
        <span><i style={{ background: '#22C55E' }} /> Reached the command center</span>
        <span><i style={{ background: '#EF4444' }} /> Stalled</span>
      </div>
    </div>
  );
}

export default function Funnel() {
  const [win, setWin] = useState('30');
  const [data, setData] = useState<FunnelResp | null>(null);
  const [error, setError] = useState('');
  const [pick, setPick] = useState<{ funnel: string; stage: string; label: string; metCount: number; total: number } | null>(null);

  useEffect(() => {
    setData(null);
    backendApi.get<FunnelResp>(`/license/admin/funnel?days=${win}`)
      .then(setData)
      .catch((err: ApiError) => setError(err.isForbidden ? 'You do not have access to the funnel.' : err.message));
  }, [win]);

  if (error) return <div className="alert alert-error">{error}</div>;

  return (
    <>
      <header className="page-head">
        <div><h1>User Funnel</h1>
          <p className="muted">Where incoming users stall on the way to using WPOS. Cohort by signup date.</p></div>
        <div className="seg" style={{ flex: '0 0 auto' }}>
          {WINDOWS.map((w) => (
            <button key={w.k} className={'seg-btn' + (win === w.k ? ' on' : '')} onClick={() => setWin(w.k)}>{w.label}</button>
          ))}
        </div>
      </header>

      {!data ? <p className="muted">Loading…</p> : (
        <>
          <div className="card">
            <h2 className="card-title">The journey</h2>
            <p className="muted small" style={{ marginTop: -6, marginBottom: 8 }}>
              Each ball is a user flowing down the funnel and splitting into the route they took — a sandbox or
              their own connected site — then into “Used command center”. Balls that stall peel off to the right.
            </p>
            <FunnelFlow data={data.registered} />
          </div>

          <div className="card">
            <h2 className="card-title">Registered users</h2>
            <p className="muted small" style={{ marginTop: -6, marginBottom: 14 }}>
              People who signed up on the app, then worked toward using the plugin.
            </p>
            <FunnelChart stages={REG_STAGES} data={data.registered}
              onPick={(stage, label, metCount, total) => setPick({ funnel: 'registered', stage, label, metCount, total })} />
          </div>

          <div className="card">
            <h2 className="card-title">Public trials</h2>
            <p className="muted small" style={{ marginTop: -6, marginBottom: 14 }}>
              Anonymous try-landing sandboxes — no account, they start already inside the product.
            </p>
            <FunnelChart stages={TRY_STAGES} data={data.publicTrial}
              onPick={(stage, label, metCount, total) => setPick({ funnel: 'publicTrial', stage, label, metCount, total })} />
          </div>

          <p className="muted small">
            Stages aren’t strictly nested: an own-site plugin user can chat without ever creating a sandbox,
            so a later count can exceed an earlier one. The −N badge is how many dropped versus the step above.
          </p>
          <p className="muted small">
            “Signed up” counts registered accounts created in the window, excluding staff and anonymous
            public-trial accounts (those are the separate Public trials funnel) — so on “All time” it will be
            lower than the Overview Accounts total, which counts every account.
          </p>
        </>
      )}

      {pick && (
        <StageAccountsModal funnel={pick.funnel} stage={pick.stage} label={pick.label}
          metCount={pick.metCount} total={pick.total} days={win}
          onClose={() => setPick(null)} />
      )}
    </>
  );
}
